import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./alertslist.css";
import {
  deleteAlert,
  getAllAlerts,
} from "../../../redux/apiCalls/alertsCall/alertsCall";
import Pagination from "../../../components/Pagination/Pagination";
import { useNavigate } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import Swal from "sweetalert2";
import { BellPlus, Pencil, Search, Siren, Trash2 } from "lucide-react";

const PAGE_SIZE = 20;

const AlertsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.currentUser);

  const alertsState = useSelector((state) => state.alerts?.alerts);
  const alertsArray = useMemo(() => {
    if (Array.isArray(alertsState)) return alertsState;
    if (alertsState && Array.isArray(alertsState.items)) return alertsState.items;
    return [];
  }, [alertsState]);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const currentDevices = useSelector(
    (state) => state.devices.currentDevices || []
  );

  const selectedIdsCsv = useMemo(
    () =>
      currentDevices
        .map((d) => d.id)
        .filter(Boolean)
        .join(","),
    [currentDevices]
  );

  const tokenRef = useRef(null);
  useEffect(() => {
    tokenRef.current = user?.accessToken || null;
  }, [user?.accessToken]);

  const lastFetchKeyRef = useRef("");
 const buildKey = (q) => JSON.stringify({ q });

useEffect(() => {
  const accessToken = tokenRef.current;
  if (!accessToken) return;

  const key = buildKey("");
  const items = alertsArray;
  const needsInitialLoad = !items || items.length === 0;

  if (needsInitialLoad || lastFetchKeyRef.current !== key) {
    lastFetchKeyRef.current = key;
    getAllAlerts(dispatch, accessToken, {
      q: "",
    });
  }
}, [dispatch, alertsArray]);

const handleSearch = async () => {
  if (!user?.accessToken) return;

  const query = search.trim();
  if (!query) return;

  await getAllAlerts(dispatch, user.accessToken, {
    q: query,
  });

  setPage(1);
};

const handleClear = async () => {
  setSearch("");
  if (!user?.accessToken) return;

  await getAllAlerts(dispatch, user.accessToken, {
    q: "",
  });

  setPage(1);
};
  // // SOLO mostrar ausencia y maximetro
  // const filteredAlerts = useMemo(() => {
  //   return alertsArray.filter((alert) => {
  //     const type = String(alert?.type || "").toLowerCase().trim();
  //     return type === "ausencia" || type === "maximetro";
  //   });
  // }, [alertsArray]);

  const totalPages = Math.max(1, Math.ceil(alertsArray.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const startIdx = (page - 1) * PAGE_SIZE;
  const endIdx = startIdx + PAGE_SIZE;
  const paginatedAlerts = alertsArray.slice(startIdx, endIdx);

  const handleDelete = async (alert) => {
    const accessToken = tokenRef.current;
    if (!accessToken) return;

    const res = await Swal.fire({
      title: "¿Eliminar alerta?",
      text: `Se eliminará "${alert?.name ?? alert?.id}".`,
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, eliminar.",
      cancelButtonText: "Cancelar",
    });

    if (!res.isConfirmed) return;

    await deleteAlert(dispatch, alert.id, accessToken);

    const q = search.trim();
    const key = buildKey(q);
    lastFetchKeyRef.current = key;

    await getAllAlerts(dispatch, accessToken, {
      q,
      device_ids: selectedIdsCsv,
    });

    const remaining = Math.max(0, alertsArray.length - 1);
    const newTotalPages = Math.max(1, Math.ceil(remaining / PAGE_SIZE));
    if (page > newTotalPages) setPage(newTotalPages);
  };

  return (
    <div>
      <div className="alerts-list__breadcrumbs">
        <p
          className="alerts-list__breadcrumb-item alerts-list__breadcrumb-item--clickable"
          onClick={() => navigate(`/`)}
        >
          <FormattedMessage id="Dashboard" defaultMessage="Escritorio" /> {">"}
        </p>
        <p
          className="alerts-list__breadcrumb-item alerts-list__breadcrumb-item--clickable"
          onClick={() => navigate(`/devices`)}
        >
          <FormattedMessage id="Devices" defaultMessage="Dispositivos" /> {">"}
        </p>
        <p className="alerts-list__breadcrumb-item">
          <FormattedMessage id="AlertsList" defaultMessage="Listado de alertas" />
        </p>
      </div>

      <div className="alerts-list__container">
        <div className="alerts-list__header">
          <div className="alerts-list__header-left">
            <i className="alerts-list__icon">
              <Siren size={18} />
            </i>
            <h5 className="alerts-list__title">Listado de alertas</h5>
          </div>

          <div className="alerts-list__header-right">
            <button
              className="alerts-list__add-btn"
              onClick={() => navigate("/new-alert")}
            >
              <BellPlus size={18} /> Nueva alerta
            </button>

            <div className="alerts-list__searchbar">
              <Search size={18} className="alerts-list__search-icon" />
              <input
                type="search"
                placeholder="Buscar por alerta..."
                className="alerts-list__search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
              {search.trim() !== "" && (
                <button
                  type="button"
                  className="alerts-list__search-clear"
                  onClick={handleClear}
                  aria-label="Limpiar búsqueda"
                  title="Limpiar"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>

        <table className="alerts-list__table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre</th>
              <th>Tipo</th>
              <th>Activa</th>
              <th className="col-actions">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {paginatedAlerts.length > 0 ? (
              paginatedAlerts.map((alert) => (
                <tr key={alert.id}>
                  <td>{alert.id}</td>
                  <td>{alert.name ?? "—"}</td>
                  <td>{alert.type ?? "—"}</td>
                  <td className="col-status">
                    <span
                      className={`status-badge ${
                        Number(alert.active) === 1
                          ? "status-badge--on"
                          : "status-badge--off"
                      }`}
                    >
                      {Number(alert.active) === 1 ? "Sí" : "No"}
                    </span>
                  </td>
                  <td className="col-actions">
                    <div className="alerts-list__actions">
                      <button
                        type="button"
                        className="alerts-list__btn alerts-list__btn--edit"
                        onClick={() => navigate(`/alerts/${alert.id}`)}
                      >
                        <Pencil size={18} className="btn-icon" />
                        Editar
                      </button>

                      <button
                        type="button"
                        className="alerts-list__btn alerts-list__btn--danger"
                        onClick={() => handleDelete(alert)}
                      >
                        <Trash2 size={18} className="btn-icon" />
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" style={{ textAlign: "center" }}>
                  No hay alertas de tipo ausencia o maximetro.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="alerts-list__pagination">
          <Pagination
            filteredItems={alertsArray}
            totalItems={alertsArray.length}
            startIdx={startIdx}
            endIdx={endIdx}
            page={page}
            setPage={setPage}
            totalPages={totalPages}
          />
        </div>
      </div>
    </div>
  );
};

export default AlertsList;